import { api } from '../client'
import { ENDPOINTS } from '../config'

export interface CheckinFilters {
    branch?: string
    session?: string
    checkedInDate?: string
    checkedOutDate?: string
    checkedOut?: 'true' | 'false'
    ageGroup?: string
    /**
     * Filter to a single child's records. `GET /checkins` accepts no `childId`,
     * but it does accept `qrCode` — and the scan already produces one, so this
     * is how a teacher reads one child's history. (The dedicated
     * `/checkins/child/:id/history` route is parent-only and 403s for teachers.)
     */
    qrCode?: string
}

export interface ListCheckinsParams extends CheckinFilters {
    page?: number
    limit?: number
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
}

const cleanParams = (
    params: Record<string, string | number | undefined>
): Record<string, string | number | undefined> => {
    const out: Record<string, string | number | undefined> = {}
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') out[k] = v
    }
    return out
}

export const CheckinsService = {
    list: (params: ListCheckinsParams = {}) =>
        api.get<unknown>(ENDPOINTS.CHECKINS.LIST(cleanParams({ ...params }))),

    aggregates: (filters: CheckinFilters = {}) =>
        api.get<unknown>(ENDPOINTS.CHECKINS.AGGREGATES(cleanParams({ ...filters }))),

    checkoutQueue: (filters: CheckinFilters = {}) =>
        api.get<unknown>(ENDPOINTS.CHECKINS.CHECKOUT_QUEUE(cleanParams({ ...filters }))),

    stale: (params: { branchId?: string; session?: string } = {}) =>
        api.get<unknown>(ENDPOINTS.CHECKINS.STALE(cleanParams({ ...params }))),

    childByQrCode: (qrCodeData: string) =>
        api.get<unknown>(ENDPOINTS.CHILDREN.BY_QR_CODE(qrCodeData)),

    checkin: (payload: {
        qrCode: string
        branch: string
        checkInBy?: string
        session?: string
    }) => api.post<unknown>(ENDPOINTS.CHECKINS.CHECKIN, payload),

    checkout: (payload: {
        qrCode: string
        branch: string
        checkinCode: string
        checkOutBy?: string
        session?: string
    }) => api.post<unknown>(ENDPOINTS.CHECKINS.CHECKOUT, payload),

    /**
     * Check in without scanning. Note the backend names this field `child`
     * (the child's _id), not `childId` as the checkout counterpart does.
     */
    checkinWithoutQr: (payload: {
        child: string
        branch?: string
        checkInBy?: string
        session?: string
    }) => api.post<unknown>(ENDPOINTS.CHECKINS.CHECKIN_WITHOUT_QR, payload),

    /** Check out without scanning — skips check-in code validation entirely. */
    checkoutWithoutQr: (payload: {
        childId: string
        branch?: string
        checkOutBy?: string
        session?: string
    }) => api.post<unknown>(ENDPOINTS.CHECKINS.CHECKOUT_WITHOUT_QR, payload),

    sendToParent: (childId: string) =>
        api.post<unknown>(ENDPOINTS.CHECKINS.SEND_TO_PARENT(childId)),

    clearQueue: (branchId: string) =>
        api.post<{ cleared: number }>(ENDPOINTS.CHECKINS.CLEAR_QUEUE, { branchId }),

    clearStale: (branchId: string, session?: string) =>
        api.post<{ updated?: number; cleared?: number }>(ENDPOINTS.CHECKINS.CLEAR_STALE, {
            branchId,
            session
        })
}
